import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0506 from "../lesson-06/lesson";

const linkLesson0506 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0506.id);

const theory = ` <h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>This lesson introduces loudspeakers as specific type of electroacoustic sound sources that are commonly present in many venues that serve a diverse range of purposes. The governing relations defined in <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a> that describe the behaviour of sound in a closed space shall be revisited in terms of introducing a set of parameters used for describing the performance of loudspeakers. This way, the interaction of a room with a loudspeaker (one or more of them) as a sound source shall be described.
<h3 class=sectionHead>Introduction</h3>
<p>As parts of sound reinforcement systems often needed in closed spaces, loudspeakers are quite common sound sources that can be put into a room to perform the intended function of emitting amplified speech or music.<br class=newline>The parameters usually used to describe loudspeakers as sound sources are somewhat different than the general parameters described in previous sections. Before investigating how loudspeakers interact with closed spaces, these parameters have to be defined and described.
<h3 class=sectionHead>Relevant loudspeaker parameters</h3>
<p>
   Sound sources such as machinery or tools are described with their sound power
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>P</mi>
   </math>
   or the corresponding sound power level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>W</mi>
         </mrow>
      </msub>
   </math>
   as required by relevant directives, since the sound power of a source is the exclusive property of that source and remains the same regardless of the environment in which the source is located. Using this data, the behaviour of such a source in terms of the resulting sound pressure level can be predicted for the operator’s position and/or the entire environment.<br class=newline>When describing a loudspeaker, there is very little use for its sound power
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>P</mi>
   </math>
   or the corresponding sound power level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>W</mi>
         </mrow>
      </msub>
   </math>
   , simply because it can be changed at will by changing the level of the input signal coming to the loudspeaker.<br class=newline>Loudspeakers are described in terms of their sensitivity and sensitivity level, power handling, on-axis gain, and radiation pattern.
<h4 class=subsectionHead>The reference axis of a loudspeaker</h4>
<p>Unlike many other sound sources, loudspeakers have a clearly defined reference axis at which their parameters are measured and to which their values are referenced. The reference axis of a loudspeaker stretches from the acoustic centre of the loudspeaker in the direction right in front of it. This direction is labelled as 0<span class=tcrm-1000>°</span>, both in azimuth and elevation.
<h4 class=subsectionHead>Sensitivity (level)</h4>
<p>
   The sensitivity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   of a loudspeaker is a constant that can be viewed as a measure of conversion efficiency from the electrical to the acoustical domain for a broadband signal. It states the sound pressure that will be obtained on axis, at a given distance, and with a given input electrical power.<br class=newline>The governing equation is
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>S</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>P</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>el</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The sound pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   will be obtained at the distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   for the input electrical power
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>el</mi>
         </mrow>
      </msub>
   </math>
   . The distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the unit distance of 1 meter. Note that the square root of electrical power is used, so that it matches the sound pressure as the “linear” variable.<br class=newline>For practicality, the input electrical power is usually set to 1 W, and the distance to 1 m.<br class=newline>From equation (1) follows the sound pressure as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>S</mi>
            <msqrt>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>el</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msqrt>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>By taking the logarithm of equation (2), one obtains the on-axis sound pressure level as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>el</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>r</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   The parameter
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </msub>
   </math>
   is the sensitivity level expressed in dB/1W/1m and is usually found in loudspeaker specifications. It denotes the sound pressure level that will be measured at 1 m in front of the loudspeaker, if the loudspeaker is fed with 1 W of electrical power. Knowing the sensitivity level, the on-axis sound pressure level can be found for any distance and any reasonable electrical power fed to the loudspeaker (within its operational limit), as shown by equation (3).
<h4 class=subsectionHead>Power handling</h4>
<p>There are several different measures of power a loudspeaker should be able to handle regarding its long-term, short-term and peak load. The most conservative one is tested by powering the loudspeaker with shaped pink noise of a given electrical power continuously for 100 hours and allowing it to rest for 24 hours after the test (see IEC 60268-5) [2]. The applied electrical power during the test is considered nominal long-term power, if the loudspeaker did not manifest any significant changes of its electrical, mechanical or acoustical properties after such a test.
<h4 class=subsectionHead>On-axis gain and radiation pattern</h4>
<p>
   The directivity factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   for sound sources in general is defined as direction-dependant, thus containing complete information on the directional properties of a source.<br class=newline>The definition for loudspeakers is somewhat different, in the sense that the directivity factor is defined only on axis of the loudspeaker (in front of it), as the on-axis gain
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>γ</mi>
   </math>
   . Since the directional properties of loudspeakers change with frequency, the on-axis gain can be given as a set of values for octave or 1/3-octave bands of interest, or a single-number value can be given, according to relevant standard IEC 60268-5 [2].<br class=newline>The definition of the on-axis gain is the same as already given in <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a> for the directivity factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   of directional sources in general (see <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a>, equations (4) and (5) and Figure 2).<br class=newline>The corresponding on-axis directivity index
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   of a loudspeaker is the difference of on-axis sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   produced by the directional source and the sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>o</mi>
         </mrow>
      </msub>
   </math>
   produced by an omnidirectional source at a given distance from the source, with both sources having the same sound power.<br class=newline>The relationship between the on-axis gain
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>γ</mi>
   </math>
   and the on-axis directivity index
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is expressed as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi>γ</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>o</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The directional behaviour of a loudspeaker in all other directions is described by its three-dimensional radiation (polar) pattern
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Γ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , which denotes the dependence of sound pressure on the direction and on frequency, referenced to the sound pressure on axis of the loudspeaker. As a result, the on-axis value of the radiation pattern shall always be 1, whereas for other directions it will be smaller.<br class=newline>The radiation pattern is measured by measuring the sound pressure level produced by the loudspeaker for a given frequency in a set of given directions, thus actually obtaining 20*log
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Γ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . In accordance with the original definition, the value of this parameter on axis is always 0 dB, while for other directions its values are negative in most cases. This is convenient because any attenuation in off-axis directions is clearly and immediately visible.<br class=newline>The relationship between the directivity factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   defined for directional sources in general, the on-axis gain
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>γ</mi>
   </math>
   , and the radiation pattern of the loudspeaker can be expressed as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Q</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>γ</mi>
            <msup>
               <mrow>
                  <mi>Γ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Equation (5) shows that the original directivity factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   has been broken down into two components: the on-axis gain
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>γ</mi>
   </math>
   that shows how much gain has been obtained in front of the loudspeaker, and the radiation pattern of the loudspeaker
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Γ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   that shows the reduction of this gain in all other directions, referenced to the on-axis gain.<br class=newline>Note that the radiation pattern of the loudspeaker is squared in equation (5) as it has been defined in terms of sound pressure, whereas the directivity factor and the on-axis gain have been defined in terms of sound intensity.<br class=newline>For a loudspeaker, it is much more convenient to describe its directional properties as shown above, because the on-axis gain and the radiation pattern can be measured separately, using dedicated procedures.
<h3 class=sectionHead>Critical distance</h3>
<p>The energy density of direct sound can now be derived from equation (6) in <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a> by introducing equation (5) into it, thus yielding
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Pγ</mi>
                  <msup>
                     <mrow>
                        <mi>Γ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ϕ</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>f</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>By equating the energy density of direct sound, expressed with equation (6), with the energy density of reverberant sound expressed by equations (7) or (10) in <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a>, one obtains the critical distance as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msub>
            <msqrt>
               <mrow>
                  <mi>γ</mi>
               </mrow>
            </msqrt>
            <mi>Γ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>Equation (7) explicitly shows the influence of direction represented with the radiation pattern of the loudspeaker, i.e. of the position of the listener relative to the loudspeaker, on the value of the critical distance.
<h3 class=sectionHead>Sound pressure level in the room produced by a loudspeaker</h3>
<p>Using the same procedure as for the general sound source, one obtains the sound pressure level of direct sound produced by the loudspeaker [1] as
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>Γ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>11</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>By equating equations (3) and (8) for the on-axis case, one obtains the sound power of the loudspeaker expressed through other parameters of the loudspeaker as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>el</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>11</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>Finally, the sound pressure level of direct sound produced by a loudspeaker is obtained by introducing equation (9) into equation (8), as
<table class=equation>
   <tr>
      <td style="font-size: 11px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>el</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>Γ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>r</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   Equation (10) shows that the sound pressure level of direct sound a loudspeaker will be able to produce will depend on the sensitivity of the loudspeaker, the input electrical power, the position of the listener relative to the loudspeaker, and the distance to the listener, i.e. there is no influence of the room.<br class=newline>Equation (9) and <a href="#" data-img-alt="Figure 1. Two loudspeakers with the same sensitivity (measured on axis) and with equal electrical power applied do not have to have the same sound power; the more directional loudspeaker will have less sound power." data-img-src="${config.ASSETS_URL}/05/07/theory/figure-1.svg">Figure 1</a> show the relationship between total sound power of a loudspeaker and its directional properties. For example, there are two loudspeakers, both with the same sensitivity level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </msub>
   </math>
   , and the same electrical power
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>el</mi>
         </mrow>
      </msub>
   </math>
   is applied to both of them. One of them is omnidirectional with directivity index
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   equal to 0, and the other one is directional with directivity index greater than 0. The omnidirectional loudspeaker will be able to produce the same sound pressure level everywhere, whereas the directional one will be able to match that only on axis, but not in other directions. In these conditions, the sound power level of the directional loudspeaker will be exactly
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   decibels lower than the one of the omnidirectional source. This is very important because it has a great influence on the level of reverberant sound. The directional loudspeaker will excite the room far less than the omnidirectional one by emitting most of the sound only to where it is needed (e.g. audience area), and emitting very little of it in unwanted directions. On the contrary, an omnidirectional source will emit sound equally in all directions. That way, the audience area still gets the required quantity of sound, but a lot of it is wasted in other directions and serves only to excite the room, thus raising the quantity of reverberant sound.
   <figure class=figure>
<p><img alt="Figure 1. Two loudspeakers with the same sensitivity (measured on axis) and with equal electrical power applied do not have to have the same sound power; the more directional loudspeaker will have less sound power." src="${config.ASSETS_URL}/05/07/theory/figure-1.svg"><figcaption class=caption>Figure 1. Two loudspeakers with the same sensitivity (measured on axis) and with equal electrical power applied do not have to have the same sound power; the more directional loudspeaker will have less sound power.</figcaption></figure>
<p>By introducing equation (9) into equation (21) from <a href="${linkLesson0506}" target="_blank">Lesson 5.6</a>, one obtains the level of reverberant sound produced by a loudspeaker as
<table class=equation>
   <tr>
      <td style="font-size: 13px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>el</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>R</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>17</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>Equation (11) shows that the level of reverberant sound produced by a loudspeaker will depend on its sensitivity and applied electrical power, on the properties of the room, and on the directional properties of the loudspeaker.
<h3 class=sectionHead>The relationship of direct and reverberant sound</h3>
<p>A successful design of a sound reinforcement system aims at achieving the highest ratio of direct to reverberant sound possible in given conditions. In other words, a good public address (PA) system should provide as much direct sound and as little reverberant sound as possible in the audience area it covers. This ensures good intelligibility and clarity of reproduced speech and music.<br class=newline>By subtracting equation (11) from equation (10), we obtain the level difference between direct and reverberant sound as
<table class=equation>
   <tr>
      <td style="font-size: 9px;">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>Γ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>R</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>17</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(12)
</table>
<p>As stated above, the aim is to maximize this difference. To achieve this, the following is crucial:
<ul class=itemize1>
   <li class=itemize>
      The value of the parameter 20*
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi class=qopname>log</mi>
         <mo>⁡</mo>
         <mi>Γ</mi>
         <mo class=MathClass-open>(</mo>
         <mi>𝜃</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ϕ</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>f</mi>
         <mo class=MathClass-close>)</mo>
      </math>
      that is essentially the radiation pattern of the loudspeaker should be as high as possible. In other words, relative to the loudspeaker, the listener should be located as close to the axis of the loudspeaker as possible (ideally in front of the loudspeaker).
   <li class=itemize>
      The value of the parameter 10*
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi class=qopname>log</mi>
         <mo>⁡</mo>
         <mi>R</mi>
      </math>
      which describes the room acoustical properties of the room through its room constant should be as high as possible. In other words, the room should be acoustically treated for optimal performance.
   <li class=itemize>
      The on-axis directivity index
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>D</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
               <mo class=MathClass-punc>,</mo>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>
      needs to be as high as possible. In other words, the more directional loudspeaker we choose, the better.
   <li class=itemize>
      The distance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>r</mi>
      </math>
      between the loudspeaker and the listener should be as short as possible.
</ul>
<p>Note that the sensitivity level of the loudspeaker and the electrical power applied to it do not change the relationship of direct and reverberant sound in any way. In other words, when it comes to overcoming the inherent properties of loudspeakers and the rooms they are in, the increase of reproduction level does not improve the quality of reproduction. To put it in simpler terms, choosing a more sensitive loudspeaker or increasing the power fed to it leads to the increase of the level of both direct and reverberant sound by the same amount, and nothing is gained.<br class=newline>On the other hand, increasing the reproduction level will improve the quality of reproduction if noise is present in the room (cheering on the stadium, train noise at the train station, etc.). In this case, the priority is to achieve sufficient reproduction level to overcome the noise, i.e. a high enough signal-to-noise ratio must be achieved if the content is to be reproduced with sufficient quality.
<h3 class=sectionHead>Multiple loudspeakers in the room</h3>
<p>
   If there are
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   identical loudspeakers in the room, all driven with the same electrical power, each of them having the sound power level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>W</mi>
         </mrow>
      </msub>
   </math>
   , their total sound power level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>W</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>total</mi>
         </mrow>
      </msub>
   </math>
   will be
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>total</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>N</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(13)
</table>
<p>Since all loudspeakers contribute to the reverberant sound in the room, its sound pressure level will also increase to
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi mathvariant=italic>total</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>N</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   The reverberant sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   is produced by each loudspeaker, and the total reverberant sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>total</mi>
         </mrow>
      </msub>
   </math>
   is produced by all loudspeakers together.<br class=newline>If the loudspeakers are not identical, total sound power level and total sound pressure level of reverberant sound can be found by logarithmic addition.<br class=newline>If the sound system in a room is distributed in space, with each sector covered by one loudspeaker, the sound pressure level of the direct sound will remain the same, as if only one loudspeaker was operational. Hence, the increase of the level of reverberant sound directly caused by using many loudspeakers leads to a lower ratio of direct-to-reverberant sound. As a result, the critical distance decreases, meaning that the regions with dominant direct sound are even smaller than they would be with only one loudspeaker active. Therefore, in highly reverberant spaces, any kind of sound reinforcement should be planned and implemented with as few loudspeakers as possible.<br class=newline>The above considerations are valid for any kind of loudspeakers, regardless of their type, model and performance.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>PA
  <dd>public address
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Ahnert_Steffen:2017 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Wolfgang Ahnert and Frank Steffen. <span class=cmti-10>Sound reinforcement engineering</span>. (1st Edition). CRC Press, 2017. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-1-138-56974-4.
   <dt id=X0-IEC60268_5 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p><span class=cmti-10>IEC 60268-5:2003 - Sound system equipment - Part 5: Loudspeakers</span>. 2003.
</dl>`;

export default theory;
